"use client";

import { z } from "zod";

import type { GlyphSessionApi } from "./glyph-account";

const URL = "/api/game/identity/glyph-session/";
const revisionSchema = z.object({
  glyphRevision: z.number().int().nonnegative(),
});
const seatSchema = revisionSchema.extend({
  rankedWallet: z.string().nullable(),
  expiresAt: z.number().nullable(),
  named: z.boolean(),
});
export type GlyphRankedSeat = z.infer<typeof seatSchema>;

/** Serializes identity writes; cancelling a browser request cannot revoke a server write. */
export class GlyphRankedSession {
  private tail: Promise<unknown> = Promise.resolve();
  private generation = 0;
  private revision: number | null = null;

  private enqueue<T>(work: () => Promise<T>): Promise<T> {
    const task = this.tail.then(work, work);
    this.tail = task.catch(() => undefined);
    return task;
  }

  bind(
    session: Pick<GlyphSessionApi, "tokens" | "privyUserId">,
    expectedWallet?: string
  ): Promise<GlyphRankedSeat | null> {
    const generation = this.generation;
    const expectedAccount = session.privyUserId;
    if (!expectedAccount) return Promise.resolve(null);
    return this.enqueue(async () => {
      // Another tab may revoke the shared cookie. Revalidate once with fresh tokens.
      for (let attempt = 0; attempt < 2; attempt++) {
        if (generation !== this.generation) return null;
        if (this.revision === null) {
          const response = await fetch(URL, {
            cache: "no-store",
            signal: AbortSignal.timeout(15_000),
          });
          if (!response.ok) throw new Error("Game identity is unavailable.");
          this.revision = revisionSchema.parse(
            await response.json()
          ).glyphRevision;
        }
        const tokens = await session.tokens();
        if (!tokens || generation !== this.generation) return null;
        const response = await fetch(URL, {
          method: "POST",
          headers: {
            authorization: `Bearer ${tokens.identityToken}`,
            "content-type": "application/json",
          },
          body: JSON.stringify({
            accessToken: tokens.accessToken,
            expectedAccount,
            glyphRevision: this.revision,
            expectedWallet,
          }),
          signal: AbortSignal.timeout(15_000),
        });
        const body: unknown = await response.json();
        if (!response.ok) {
          const stale = z
            .object({ code: z.literal("STALE_IDENTITY") })
            .safeParse(body);
          if (response.status === 409 && stale.success) {
            this.revision = null;
            continue;
          }
          throw new Error("Game identity could not be restored.");
        }
        const seat = seatSchema.parse(body);
        return generation === this.generation &&
          (!seat.expiresAt || seat.expiresAt > Date.now())
          ? seat
          : null;
      }
      throw new Error("Game identity changed. Try again.");
    });
  }

  /** Called synchronously on logout/account change, before any new binding is queued. */
  revoke(): Promise<void> {
    ++this.generation;
    return this.enqueue(async () => {
      if (this.revision === null) return;
      const revision = this.revision;
      const response = await fetch(URL, {
        method: "DELETE",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ glyphRevision: revision }),
        signal: AbortSignal.timeout(15_000),
        keepalive: true,
      });
      if (!response.ok && response.status !== 409)
        throw new Error("Game sign-out could not finish. Try again.");
      this.revision = null;
    });
  }
}
